import { useState, FormEvent, ChangeEvent, useRef } from "react";
import { useLocation } from "react-router";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { INTERNAL_ROUTES } from "../../../../constants/routes";
import { useSmartNavigation } from "../../../../helpers/navigation";

import S from "../UserNavBar.module.scss";

const SESSION_STORAGE_KEY = "catalogFiltersAndSearch";

export const NavSearchForm = ({
  menuItemCount,
  testId = "nav-search",
}: {
  menuItemCount: number;
  /** Prefix used to build data-testids for the form, input, and buttons. */
  testId?: string;
}) => {
  const [value, setValue] = useState<string>("");
  const formRef = useRef<HTMLFormElement>(null);
  const { navigateTo } = useSmartNavigation();
  const location = useLocation();

  const isOnCatalogPage = location.pathname === INTERNAL_ROUTES.Learn.routes.catalog.route;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value);

  const navigateToCatalogWithSearch = (searchQuery: string) => {
    // Store search query and clear filters in sessionStorage
    sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({
        searchQuery,
        focusSearch: true,
        filters: {
          focus: [],
          difficulty: [],
          release: [],
          contentType: [],
          languages: [],
          technologies: [],
          cwe: [],
        },
      })
    );

    navigateTo(INTERNAL_ROUTES.Learn.routes.catalog.route);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!value.length) return;

    navigateToCatalogWithSearch(value);

    // Clear the input
    setValue("");
  };

  const handleIconClick = () => {
    formRef.current?.requestSubmit();
  };

  const handleCompactSearchClick = () => {
    navigateToCatalogWithSearch("");
  };

  return (
    <>
      {isOnCatalogPage && (
        <Tooltip
          target="#nav-search-form"
          content="Use the search box on the page below"
          position="bottom"
        />
      )}
      <form
        id="nav-search-form"
        role="search"
        onSubmit={handleSubmit}
        className={S.navSearchForm}
        data-testid={`${testId}-form`}
        data-menu-count={menuItemCount}
        style={{ display: "inline-block" }}
        ref={formRef}
      >
        <IconField iconPosition="right">
          <InputText
            size="small"
            id="nav-search-field"
            aria-label="Search the catalog"
            data-testid={`${testId}-input`}
            style={{ width: "220px", paddingLeft: "12px" }}
            placeholder="What do you want to learn?"
            value={value}
            onChange={handleChange}
            autoComplete="off"
            disabled={isOnCatalogPage}
            pt={{ root: { style: { fontSize: "0.75rem" } } }}
          />
          <InputIcon
            className="pi pi-search"
            onClick={handleIconClick}
            style={{ cursor: isOnCatalogPage ? "not-allowed" : "pointer" }}
          />
        </IconField>
      </form>
      <Button
        icon="pi pi-search"
        rounded
        text
        aria-label="Search"
        data-testid={`${testId}-compact-button`}
        className={`${S.compactSearchButton} ${isOnCatalogPage ? "nav-search-disabled" : ""}`}
        data-menu-count={menuItemCount}
        onClick={handleCompactSearchClick}
        style={{ color: "white" }}
        disabled={isOnCatalogPage}
      />
    </>
  );
};
